import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  ArrowLeft, Save, Send, CheckCircle2, XCircle, BookCheck,
  RotateCcw, Ban, Trash2, AlertTriangle, CheckCircle,
  FileText, History, Paperclip, ShieldCheck, Search, X, Printer,
  ExternalLink, TrendingDown,
} from 'lucide-react';

import { bankWithdrawalApi } from '@/api/bankWithdrawal.api';
import { bankAccountApi } from '@/api/bank.api';
import { accountApi } from '@/api/account.api';
import { exchangeRateApi } from '@/api/exchangeRate.api';
import { MasterReportPreview } from '@/components/master/report/MasterReportPreview';
import { BankWithdrawalVoucher } from '@/components/banking/BankWithdrawalVoucher';
import { exportToExcel } from '@/utils/reportExport';
import { usePermission } from '@/hooks/usePermission';
import { Permissions } from '@/constants/permissions';
import { AppRoutes } from '@/constants/routes';
import { Spinner } from '@/components/ui/Spinner';
import { MasterSearchModal } from '@/components/master/MasterSearchModal';
import { AccountLedgerButton, ledgerShortcut } from '@/components/erp/AccountLedgerButton';
import { ERPContextBar } from '@/components/erp/ERPContextBar';
import { WorkflowStatusPanel } from '@/components/erp/WorkflowStatusPanel';
import { AttachmentPanel } from '@/components/erp/AttachmentPanel';
import { AuditViewer } from '@/components/erp/AuditViewer';
import { setErpContext, clearErpContext } from '@/state/erpContextSlice';
import { cn } from '@/utils/cn';

import type { RootState } from '@/state/store';
import { costCentresForLocation } from '@/utils/costCentreScope';
import type { SearchColumn } from '@/components/master/MasterSearchModal';
import type { BankAccountListDto } from '@/types/bank.types';
import type { AccountListDto } from '@/types/account.types';
import type {
  BankWithdrawalDto,
  BankWithdrawalWorkflowStatus,
  CreateBankWithdrawalRequest,
  UpdateBankWithdrawalRequest,
} from '@/types/bankWithdrawal.types';
import { BNK_WITHDRAWAL_WORKFLOW_STATUS_COLORS } from '@/types/bankWithdrawal.types';

// ── Constants ──────────────────────────────────────────────────────────────────

const WITHDRAWAL_TYPES = ['Cash', 'Cheque', 'Transfer', 'Other'];
const PAYMENT_METHODS  = ['Cash', 'Cheque', 'BankTransfer', 'OnlineBanking', 'Other'];

// ── Search columns ─────────────────────────────────────────────────────────────

const BANK_ACCOUNT_COLUMNS: SearchColumn<BankAccountListDto>[] = [
  { key: 'accountNumber', label: 'Account No.', className: 'w-32 font-mono text-teal-700 dark:text-teal-400' },
  { key: 'accountName',   label: 'Account Name' },
  { key: 'currencyCode',  label: 'CCY', className: 'w-14 font-mono text-slate-500 dark:text-gray-400' },
  { key: 'accountType',   label: 'Type', className: 'w-20' },
];

const GL_ACCOUNT_COLUMNS: SearchColumn<AccountListDto>[] = [
  { key: 'code',        label: 'Code',         className: 'w-28 font-mono text-teal-700 dark:text-teal-400' },
  { key: 'name',        label: 'Account Name' },
  { key: 'accountType', label: 'Type',         className: 'w-24' },
];

// ── Tabs ───────────────────────────────────────────────────────────────────────

const TABS = [
  { key: 'details',     label: 'Details',    icon: FileText    },
  { key: 'workflow',    label: 'Workflow',    icon: History     },
  { key: 'attachments', label: 'Attachments', icon: Paperclip   },
  { key: 'audit',       label: 'Audit Log',   icon: ShieldCheck },
] as const;
type Tab = (typeof TABS)[number]['key'];

// ── Action modal ───────────────────────────────────────────────────────────────

interface ActionModalProps {
  title: string; placeholder: string; required: boolean;
  onConfirm: (text: string) => void; onCancel: () => void;
}

const ActionModal = ({ title, placeholder, required, onConfirm, onCancel }: ActionModalProps) => {
  const [text, setText] = useState('');
  const ref = useRef<HTMLTextAreaElement>(null);
  useEffect(() => { ref.current?.focus(); }, []);
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-md p-6">
        <h3 className="text-base font-semibold text-slate-900 dark:text-white mb-3">{title}</h3>
        <textarea ref={ref} value={text} onChange={e => setText(e.target.value)} placeholder={placeholder} rows={4}
          className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg text-sm
                     bg-white dark:bg-slate-700 text-slate-900 dark:text-white resize-none
                     focus:ring-2 focus:ring-teal-500 outline-none" />
        <div className="flex gap-3 mt-4 justify-end">
          <button onClick={onCancel}
            className="px-4 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-600
                       hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300">
            Cancel
          </button>
          <button onClick={() => onConfirm(text)} disabled={required && !text.trim()}
            className="px-4 py-2 text-sm rounded-lg bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50">
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
};

// ── GL Account Picker ──────────────────────────────────────────────────────────

interface GlAccountPickerProps {
  label:       string;
  accountId:   string;
  accountCode: string;
  accountName: string;
  required?:   boolean;
  editable:    boolean;
  onOpen:      () => void;
  onClear:     () => void;
}

const GlAccountPicker = ({ label, accountId, accountCode, accountName, required, editable, onOpen, onClear }: GlAccountPickerProps) => (
  <div onKeyDownCapture={ledgerShortcut(accountId)}>
    <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <div
      tabIndex={accountId ? 0 : -1}
      onClick={() => editable && onOpen()}
      className={cn(
        'flex items-center gap-2 h-9 px-3 rounded-lg border text-sm transition-colors',
        'bg-white dark:bg-slate-700 border-slate-200 dark:border-slate-600',
        editable ? 'cursor-pointer hover:border-teal-400' : 'cursor-default',
        !accountId && 'text-slate-400',
      )}
    >
      {accountId ? (
        <>
          <span className="font-mono text-xs font-semibold text-teal-700 dark:text-teal-400">{accountCode}</span>
          <span className="text-slate-700 dark:text-slate-300 truncate flex-1 text-xs">{accountName}</span>
          <AccountLedgerButton accountId={accountId} />
          {editable && (
            <button type="button" onClick={e => { e.stopPropagation(); onClear(); }}
              className="text-slate-300 hover:text-red-500"><X size={12} /></button>
          )}
        </>
      ) : (
        <>
          {editable && <Search size={12} className="text-slate-300" />}
          <span className="text-xs italic">{editable ? 'Select GL account…' : '—'}</span>
        </>
      )}
    </div>
  </div>
);

const fieldClass = (disabled: boolean) => cn(
  'w-full px-3 py-2 text-sm border rounded-lg outline-none transition-colors',
  'bg-white dark:bg-slate-700 text-slate-900 dark:text-white',
  'border-slate-200 dark:border-slate-600',
  disabled
    ? 'bg-slate-50 dark:bg-slate-800 text-slate-500 cursor-default'
    : 'focus:ring-2 focus:ring-teal-500 focus:border-transparent',
);

const fmt = (n: number) => n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ── Main Component ─────────────────────────────────────────────────────────────

export const BankWithdrawalFormPage = () => {
  const { id }   = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isNew    = !id;

  const { hasPermission } = usePermission();
  const canEdit    = hasPermission(Permissions.BNK_WITHDRAWAL_EDIT);
  const canSubmit  = hasPermission(Permissions.BNK_WITHDRAWAL_SUBMIT);
  const canApprove = hasPermission(Permissions.BNK_WITHDRAWAL_APPROVE);
  const canReject  = hasPermission(Permissions.BNK_WITHDRAWAL_REJECT);
  const canPost    = hasPermission(Permissions.BNK_WITHDRAWAL_POST);
  const canReverse = hasPermission(Permissions.BNK_WITHDRAWAL_REVERSE);
  const canCancel  = hasPermission(Permissions.BNK_WITHDRAWAL_CANCEL);
  const canDelete  = hasPermission(Permissions.BNK_WITHDRAWAL_DELETE);
  const canPrint   = hasPermission(Permissions.BNK_WITHDRAWAL_PRINT);
  const canAddDoc  = hasPermission(Permissions.DOC_ATTACHMENT_ADD);
  const canDelDoc  = hasPermission(Permissions.DOC_ATTACHMENT_DELETE);

  const authUser   = useSelector((s: RootState) => s.auth.user);
  const session    = useSelector((s: RootState) => s.session.session);
  const accessibleCostCenters = useSelector((s: RootState) => s.auth.accessibleCostCenters);

  const today = new Date().toISOString().split('T')[0];
  const baseCurrency = authUser?.baseCurrencyCode ?? session?.companyCurrency ?? 'USD';

  // ── Form state ────────────────────────────────────────────────────────────
  const [withdrawalDate,   setWithdrawalDate]   = useState(today);
  const [postingDate,      setPostingDate]       = useState(today);
  const [bankAccountId,    setBankAccountId]     = useState('');
  const [bankAccountNumber, setBankAccountNumber] = useState('');
  const [bankAccountName,  setBankAccountName]   = useState('');
  const [bankAccountCode,  setBankAccountCode]   = useState('');
  const [contraAccountId,  setContraAccountId]   = useState('');
  const [contraAccountCode, setContraAccountCode] = useState('');
  const [contraAccountName, setContraAccountName] = useState('');
  const [amount,           setAmount]            = useState<number>(0);
  const [exchangeRate,     setExchangeRate]      = useState(1);
  const [currencyCode,     setCurrencyCode]      = useState(baseCurrency);
  const [withdrawalType,   setWithdrawalType]    = useState('Cash');
  const [paymentMethod,    setPaymentMethod]     = useState('Cash');
  const [chequeNumber,     setChequeNumber]      = useState('');
  const [chequeDate,       setChequeDate]        = useState('');
  const [description,      setDescription]       = useState('');
  const [reference,        setReference]         = useState('');
  const [costCenterId,     setCostCenterId]      = useState('');

  const baseAmount = amount * exchangeRate;

  const [bankOpen,   setBankOpen]   = useState(false);
  const [contraOpen, setContraOpen] = useState(false);

  const [withdrawal, setWithdrawal] = useState<BankWithdrawalDto | null>(null);
  const [loading,    setLoading]    = useState(!isNew);
  const [saving,     setSaving]     = useState(false);
  const [error,      setError]      = useState<string | null>(null);
  const [success,    setSuccess]    = useState<string | null>(null);
  const [tab,        setTab]        = useState<Tab>('details');
  const [modal,      setModal]      = useState<'approve' | 'reject' | 'reverse' | 'cancel' | 'delete' | null>(null);
  const [printOpen,  setPrintOpen]  = useState(false);

  useEffect(() => {
    if (!isNew) return;
    setCostCenterId(session?.costCenterId ?? '');
  }, [isNew, session?.costCenterId]);

  useEffect(() => {
    if (!currencyCode || currencyCode === baseCurrency) {
      if (isNew) setExchangeRate(1);
      return;
    }
    exchangeRateApi.getList({ fromCurrencyCode: baseCurrency, toCurrencyCode: currencyCode, pageSize: 10 })
      .then(res => {
        const items = res.data?.data?.items ?? [];
        const sorted = items
          .filter((r: { effectiveDate: string }) => r.effectiveDate <= withdrawalDate)
          .sort((a: { effectiveDate: string }, b: { effectiveDate: string }) => b.effectiveDate.localeCompare(a.effectiveDate));
        if (sorted.length > 0) setExchangeRate(sorted[0].rate);
        else if (items.length > 0) setExchangeRate(items[0].rate);
      }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currencyCode, withdrawalDate]);

  const fetchBankAccounts = async (search: string, page: number, pageSize: number) => {
    const res = await bankAccountApi.getList({ search, pageNumber: page, pageSize });
    const d = res.data.data;
    return { items: d?.items ?? [], totalCount: d?.totalCount ?? 0 };
  };

  const fetchGlAccounts = async (search: string, page: number, pageSize: number) => {
    const res = await accountApi.getList({ search, pageNumber: page, pageSize, isActive: true } as Parameters<typeof accountApi.getList>[0]);
    const d = res.data.data;
    return { items: d?.items ?? [], totalCount: d?.totalCount ?? 0 };
  };

  // ── Load ──────────────────────────────────────────────────────────────────
  const loadWithdrawal = useCallback(async () => {
    if (isNew || !id) return;
    setLoading(true); setError(null);
    try {
      const res = await bankWithdrawalApi.getById(id);
      const w = res.data?.data;
      if (!w) throw new Error('Withdrawal not found');
      setWithdrawal(w);

      setWithdrawalDate(w.withdrawalDate.split('T')[0]);
      setPostingDate(w.postingDate.split('T')[0]);
      setBankAccountId(w.bankAccountId);
      setBankAccountCode(w.bankAccountCode);
      setBankAccountName(w.bankAccountName);
      setContraAccountId(w.contraAccountId ?? '');
      setContraAccountCode(w.contraAccountCode ?? '');
      setContraAccountName(w.contraAccountName ?? '');
      setCurrencyCode(w.currencyCode);
      setExchangeRate(w.exchangeRate);
      setAmount(w.amount);
      setWithdrawalType(w.withdrawalType);
      setDescription(w.narration ?? '');
      setReference(w.reference ?? '');
      setCostCenterId(w.costCenterId ?? '');

      bankAccountApi.getById(w.bankAccountId)
        .then(r => { if (r.data?.data) setBankAccountNumber(r.data.data.accountNumber); })
        .catch(() => {});

      dispatch(setErpContext({
        companyCode:         authUser?.companyId?.substring(0, 8).toUpperCase() ?? 'COMPANY',
        companyName:         '',
        locationCode:        null, locationName: null,
        costCenterCode:      null, costCenterName: null,
        financialYearCode:   w.financialYearCode,
        financialPeriodCode: w.financialPeriodCode,
        transactionTypeCode: 'BNK-WDR',
        documentNumber:      w.withdrawalNumber,
        currencyCode:        w.currencyCode,
        exchangeRate:        w.exchangeRate,
      }));
    } catch (err: unknown) {
      setError((err as Error).message ?? 'Failed to load withdrawal');
    } finally {
      setLoading(false);
    }
  }, [id, isNew, dispatch, authUser]);

  useEffect(() => { loadWithdrawal(); }, [loadWithdrawal]);
  useEffect(() => { return () => { dispatch(clearErpContext()); }; }, [dispatch]);

  const status = (withdrawal?.workflowStatus ?? 'Draft') as BankWithdrawalWorkflowStatus;
  const isDraftEditable = isNew || status === 'Draft' || status === 'Rejected';
  const isEditable = isDraftEditable && (isNew ? hasPermission(Permissions.BNK_WITHDRAWAL_CREATE) : canEdit);

  // ── Save ──────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    setError(null);
    if (!bankAccountId) { setError('Please select a bank account.'); return; }
    if (amount <= 0)    { setError('Amount must be greater than zero.'); return; }

    setSaving(true);
    try {
      if (isNew) {
        const payload: CreateBankWithdrawalRequest = {
          companyId:           authUser?.companyId ?? '',
          locationId:          null,
          costCenterId:        costCenterId || null,
          financialYearCode:   session?.financialYearCode ?? '',
          financialPeriodCode: session?.financialPeriodCode ?? '',
          withdrawalDate,
          postingDate,
          bankAccountId,
          withdrawalType,
          contraAccountId: contraAccountId || null,
          currencyCode,
          exchangeRate,
          amount,
          reference:  reference || null,
          narration:  description || null,
        };
        const res = await bankWithdrawalApi.create(payload);
        const newId = res.data?.data?.id;
        setSuccess('Withdrawal created successfully.');
        setTimeout(() => navigate(`/banking/withdrawals/${newId}`), 800);
      } else if (withdrawal) {
        const payload: UpdateBankWithdrawalRequest = {
          withdrawalDate,
          postingDate,
          bankAccountId,
          bankAccountCode,
          bankAccountName,
          bankGLAccountId:  null,
          withdrawalType,
          contraAccountId:   contraAccountId || null,
          contraAccountCode: contraAccountCode || null,
          contraAccountName: contraAccountName || null,
          currencyCode,
          exchangeRate,
          amount,
          reference:  reference || null,
          narration:  description || null,
        };
        await bankWithdrawalApi.update(withdrawal.id, payload);
        setSuccess('Withdrawal updated.');
        setTimeout(() => { setSuccess(null); loadWithdrawal(); }, 800);
      }
    } catch (err: unknown) {
      setError((err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Failed to save withdrawal.');
    } finally {
      setSaving(false);
    }
  };

  // ── Workflow ───────────────────────────────────────────────────────────────
  const doAction = async (action: () => Promise<void>, successMsg: string) => {
    setError(null); setSaving(true);
    try {
      await action();
      setSuccess(successMsg);
      setTimeout(() => { setSuccess(null); loadWithdrawal(); }, 1200);
    } catch (err: unknown) {
      setError((err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Action failed.');
    } finally { setSaving(false); }
  };

  const handleSubmit  = () => doAction(async () => { await bankWithdrawalApi.submit(withdrawal!.id); }, 'Withdrawal submitted for approval.');
  const handlePost    = () => doAction(async () => { await bankWithdrawalApi.post(withdrawal!.id); }, 'Withdrawal posted to general ledger.');
  const handleApprove = (r: string) => doAction(async () => { await bankWithdrawalApi.approve(withdrawal!.id, r); }, 'Withdrawal approved.');
  const handleReject  = (r: string) => doAction(async () => { await bankWithdrawalApi.reject(withdrawal!.id, r); }, 'Withdrawal rejected.');
  const handleCancel  = (r: string) => doAction(async () => { await bankWithdrawalApi.cancel(withdrawal!.id, r); }, 'Withdrawal cancelled.');
  const handleReverse = (narr: string) => doAction(async () => {
    const res = await bankWithdrawalApi.reverse(withdrawal!.id, today, today, narr || undefined);
    const revId = res.data?.data?.id;
    setTimeout(() => navigate(`/banking/withdrawals/${revId}`), 1200);
  }, 'Reversal withdrawal created.');

  const handleDelete = async () => {
    if (!withdrawal) return;
    setSaving(true); setError(null);
    try {
      await bankWithdrawalApi.delete(withdrawal.id);
      navigate(AppRoutes.BANK_WITHDRAWALS, { replace: true });
    } catch (e: unknown) {
      setError((e as { response?: { data?: { message?: string } }; message?: string })?.response?.data?.message
        ?? (e as { message?: string })?.message ?? 'Delete failed.');
    } finally { setSaving(false); }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-full"><Spinner size="lg" /></div>;
  }

  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-900 overflow-hidden">
      <ERPContextBar />

      {/* Toolbar */}
      <div className="flex-shrink-0 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-4 py-2.5">
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => navigate(AppRoutes.BANK_WITHDRAWALS)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-600 dark:text-slate-400
                       hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors">
            <ArrowLeft size={14} /> Back
          </button>
          <div className="w-px h-6 bg-slate-200 dark:bg-slate-600" />

          {isEditable && (
            <button onClick={handleSave} disabled={saving}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-teal-600 hover:bg-teal-700
                         text-white rounded-lg disabled:opacity-50 transition-colors font-medium">
              {saving ? <Spinner size="sm" /> : <Save size={14} />} Save Draft
            </button>
          )}
          {!isNew && status === 'Draft' && canSubmit && (
            <button onClick={handleSubmit} disabled={saving}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-sky-600 hover:bg-sky-700
                         text-white rounded-lg disabled:opacity-50 transition-colors font-medium">
              <Send size={14} /> Submit for Approval
            </button>
          )}
          {!isNew && (status === 'Submitted' || status === 'PendingApproval') && canApprove && (
            <button onClick={() => setModal('approve')} disabled={saving}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-green-600 hover:bg-green-700
                         text-white rounded-lg disabled:opacity-50 transition-colors font-medium">
              <CheckCircle2 size={14} /> Approve
            </button>
          )}
          {!isNew && (status === 'Submitted' || status === 'PendingApproval') && canReject && (
            <button onClick={() => setModal('reject')} disabled={saving}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-red-600 hover:bg-red-700
                         text-white rounded-lg disabled:opacity-50 transition-colors font-medium">
              <XCircle size={14} /> Reject
            </button>
          )}
          {!isNew && status === 'Approved' && canPost && (
            <button onClick={handlePost} disabled={saving}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-emerald-600 hover:bg-emerald-700
                         text-white rounded-lg disabled:opacity-50 transition-colors font-medium">
              <BookCheck size={14} /> Post to Ledger
            </button>
          )}
          {!isNew && status === 'Posted' && canReverse && !withdrawal?.reversalWithdrawalId && (
            <button onClick={() => setModal('reverse')} disabled={saving}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-amber-600 hover:bg-amber-700
                         text-white rounded-lg disabled:opacity-50 transition-colors font-medium">
              <RotateCcw size={14} /> Reverse
            </button>
          )}
          {!isNew && !['Posted', 'Cancelled', 'Reversed'].includes(status) && canCancel && (
            <button onClick={() => setModal('cancel')} disabled={saving}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-rose-600 hover:bg-rose-700
                         text-white rounded-lg disabled:opacity-50 transition-colors font-medium">
              <Ban size={14} /> Cancel
            </button>
          )}
          {!isNew && status === 'Draft' && canDelete && (
            <button onClick={() => setModal('delete')} disabled={saving}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-red-600 hover:bg-red-700
                         text-white rounded-lg disabled:opacity-50 transition-colors font-medium">
              <Trash2 size={14} /> Delete
            </button>
          )}
          {!isNew && withdrawal && canPrint && (
            <button onClick={() => setPrintOpen(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-600 dark:text-slate-400
                         hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors">
              <Printer size={14} /> Print
            </button>
          )}
          {!isNew && (
            <div className="ml-auto flex items-center gap-2">
              {withdrawal?.isReversal && (
                <span className="px-2 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 text-xs font-semibold rounded-full">Reversal</span>
              )}
              <span className={cn('px-3 py-1 rounded-full text-xs font-semibold',
                BNK_WITHDRAWAL_WORKFLOW_STATUS_COLORS[status] ?? 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-gray-400')}>
                {status}
              </span>
            </div>
          )}
        </div>

        {error && (
          <div className="mt-2 flex items-start gap-2 px-3 py-2 bg-red-50 dark:bg-red-900/20
                          border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-400">
            <AlertTriangle size={14} className="shrink-0 mt-0.5" /><span>{error}</span>
          </div>
        )}
        {success && (
          <div className="mt-2 flex items-center gap-2 px-3 py-2 bg-green-50 dark:bg-green-900/20
                          border border-green-200 dark:border-green-800 rounded-lg text-sm text-green-700 dark:text-green-400">
            <CheckCircle size={14} className="shrink-0" /><span>{success}</span>
          </div>
        )}
      </div>

      {/* Header */}
      <div className="flex-shrink-0 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 py-4">
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center gap-2">
            <TrendingDown size={18} className="text-teal-600" />
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
              {isNew ? 'New Bank Withdrawal' : `Bank Withdrawal ${withdrawal?.withdrawalNumber ?? ''}`}
            </h2>
          </div>
          <div className="flex items-center gap-3">
            {!isNew && withdrawal?.originalWithdrawalId && (
              <button onClick={() => navigate(`/banking/withdrawals/${withdrawal.originalWithdrawalId}`)}
                className="text-xs text-teal-600 hover:underline flex items-center gap-1">
                View Original <ExternalLink size={10} />
              </button>
            )}
            {!isNew && withdrawal?.reversalWithdrawalId && (
              <button onClick={() => navigate(`/banking/withdrawals/${withdrawal.reversalWithdrawalId}`)}
                className="text-xs text-amber-600 hover:underline flex items-center gap-1">
                View Reversal <ExternalLink size={10} />
              </button>
            )}
            {!isNew && withdrawal?.sourceGlJournalId && (
              <button onClick={() => navigate(`/gl/journals/${withdrawal.sourceGlJournalId}`)}
                className="text-xs text-emerald-600 hover:underline flex items-center gap-1">
                View GL Journal <ExternalLink size={10} />
              </button>
            )}
          </div>
        </div>
        {!isNew && withdrawal && (
          <p className="text-xs text-slate-400 mb-0">Created {new Date(withdrawal.createdDate).toLocaleString()}</p>
        )}
      </div>

      {/* Tabs */}
      <div className="flex-shrink-0 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6">
        <div className="flex">
          {TABS.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={cn(
                'flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors',
                tab === t.key
                  ? 'border-teal-600 text-teal-600'
                  : 'border-transparent text-slate-500 dark:text-gray-400 hover:text-slate-700 dark:hover:text-slate-300 hover:border-slate-300',
              )}>
              <t.icon size={14} />{t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab body */}
      <div className="flex-1 overflow-auto">

        {tab === 'details' && (
          <div className="p-6">
            <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">

                {/* Transaction Date */}
                <div>
                  <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                    Transaction Date <span className="text-red-500">*</span>
                  </label>
                  <input type="date" value={withdrawalDate} onChange={e => setWithdrawalDate(e.target.value)}
                    disabled={!isEditable} className={fieldClass(!isEditable)} />
                </div>

                {/* Posting Date */}
                <div>
                  <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                    Posting Date <span className="text-red-500">*</span>
                  </label>
                  <input type="date" value={postingDate} onChange={e => setPostingDate(e.target.value)}
                    disabled={!isEditable} className={fieldClass(!isEditable)} />
                </div>

                {/* Withdrawal Type */}
                <div>
                  <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                    Withdrawal Type <span className="text-red-500">*</span>
                  </label>
                  <select value={withdrawalType} onChange={e => setWithdrawalType(e.target.value)}
                    disabled={!isEditable} className={fieldClass(!isEditable)}>
                    {WITHDRAWAL_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>

                {/* Bank Account */}
                <div>
                  <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                    Bank Account <span className="text-red-500">*</span>
                  </label>
                  <div
                    onClick={() => isEditable && setBankOpen(true)}
                    className={cn(
                      'flex items-center gap-2 h-9 px-3 rounded-lg border text-sm transition-colors',
                      'bg-white dark:bg-slate-700 border-slate-200 dark:border-slate-600',
                      isEditable ? 'cursor-pointer hover:border-teal-400' : 'cursor-default',
                      !bankAccountId && 'text-slate-400',
                    )}
                  >
                    {bankAccountId ? (
                      <>
                        <span className="font-mono text-xs font-semibold text-teal-700 dark:text-teal-400">{bankAccountNumber || bankAccountCode}</span>
                        <span className="text-slate-700 dark:text-slate-300 truncate flex-1 text-xs">{bankAccountName}</span>
                        {isEditable && (
                          <button type="button" onClick={e => { e.stopPropagation(); setBankAccountId(''); setBankAccountNumber(''); setBankAccountName(''); setBankAccountCode(''); }}
                            className="text-slate-300 hover:text-red-500"><X size={12} /></button>
                        )}
                      </>
                    ) : (
                      <>
                        {isEditable && <Search size={12} className="text-slate-300" />}
                        <span className="text-xs italic">{isEditable ? 'Select bank account…' : '—'}</span>
                      </>
                    )}
                  </div>
                </div>

                {/* Contra Account (GL) */}
                <div>
                  <GlAccountPicker
                    label="Contra Account (GL)"
                    accountId={contraAccountId}
                    accountCode={contraAccountCode}
                    accountName={contraAccountName}
                    editable={isEditable}
                    onOpen={() => setContraOpen(true)}
                    onClear={() => { setContraAccountId(''); setContraAccountCode(''); setContraAccountName(''); }}
                  />
                </div>

                {/* Amount */}
                <div>
                  <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                    Amount <span className="text-red-500">*</span>
                  </label>
                  <input type="number" value={amount || ''} onChange={e => setAmount(parseFloat(e.target.value) || 0)}
                    disabled={!isEditable} step="0.01" min="0"
                    className={cn(fieldClass(!isEditable), 'text-right font-mono')} />
                </div>

                {/* Currency */}
                <div>
                  <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Currency</label>
                  <input value={currencyCode} onChange={e => setCurrencyCode(e.target.value.toUpperCase())}
                    disabled={!isEditable} maxLength={10}
                    className={cn(fieldClass(!isEditable), 'font-mono uppercase')} />
                </div>

                {/* Exchange Rate */}
                <div>
                  <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Exchange Rate</label>
                  <input type="number" value={exchangeRate} onChange={e => setExchangeRate(parseFloat(e.target.value) || 1)}
                    disabled={!isEditable} step="0.000001" min="0.000001" className={fieldClass(!isEditable)} />
                </div>

                {/* Base Amount */}
                <div>
                  <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                    Base Amount <span className="text-xs text-slate-400">(computed)</span>
                  </label>
                  <div className={cn(fieldClass(true), 'font-mono text-right font-semibold text-teal-700 dark:text-teal-400')}>
                    {fmt(baseAmount)}
                  </div>
                </div>

                {/* Payment Method */}
                <div>
                  <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Payment Method</label>
                  <select value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)}
                    disabled={!isEditable} className={fieldClass(!isEditable)}>
                    {PAYMENT_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>

                {/* Cheque Number (conditional) */}
                {withdrawalType === 'Cheque' && (
                  <div>
                    <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Cheque Number</label>
                    <input value={chequeNumber} onChange={e => setChequeNumber(e.target.value)}
                      disabled={!isEditable} placeholder="Cheque #…" className={fieldClass(!isEditable)} />
                  </div>
                )}

                {/* Cheque Date (conditional) */}
                {withdrawalType === 'Cheque' && (
                  <div>
                    <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Cheque Date</label>
                    <input type="date" value={chequeDate} onChange={e => setChequeDate(e.target.value)}
                      disabled={!isEditable} className={fieldClass(!isEditable)} />
                  </div>
                )}

                {/* Reference */}
                <div>
                  <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Reference</label>
                  <input value={reference} onChange={e => setReference(e.target.value)}
                    disabled={!isEditable} placeholder="Optional" className={fieldClass(!isEditable)} />
                </div>

                {/* Cost Centre */}
                <div>
                  <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Cost Centre</label>
                  <select value={costCenterId} onChange={e => setCostCenterId(e.target.value)}
                    disabled={!isEditable} className={fieldClass(!isEditable)}>
                    <option value="">— None —</option>
                    {costCentresForLocation(accessibleCostCenters, session?.locationId).map(cc => <option key={cc.id} value={cc.id}>{cc.code} — {cc.name}</option>)}
                  </select>
                </div>

                {/* Financial Period */}
                <div>
                  <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Financial Period</label>
                  <div className={fieldClass(true)}>
                    {withdrawal?.financialPeriodCode ?? session?.financialPeriodCode ?? '—'}
                  </div>
                </div>

                {/* Description */}
                <div className="lg:col-span-3">
                  <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Description</label>
                  <textarea value={description} onChange={e => setDescription(e.target.value)}
                    disabled={!isEditable} rows={2} placeholder="Describe this withdrawal…"
                    className={cn(fieldClass(!isEditable), 'resize-none')} />
                </div>

              </div>
            </div>
          </div>
        )}

        {tab === 'workflow' && (
          <div className="p-6">
            {!isNew && withdrawal ? (
              <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5">
                <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-4">Approval Status</h3>
                <WorkflowStatusPanel entityName="BankWithdrawal" entityId={withdrawal.id} />
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-48 text-slate-400">
                <History size={28} className="mb-2 opacity-30" />
                <p className="text-sm">Save the withdrawal first to track workflow.</p>
              </div>
            )}
          </div>
        )}

        {tab === 'attachments' && (
          <div className="p-6">
            {!isNew && withdrawal ? (
              <AttachmentPanel entityName="BankWithdrawal" entityId={withdrawal.id} canAdd={canAddDoc} canDelete={canDelDoc} />
            ) : (
              <div className="flex flex-col items-center justify-center h-48 text-slate-400">
                <Paperclip size={28} className="mb-2 opacity-30" />
                <p className="text-sm">Save the withdrawal first to add attachments.</p>
              </div>
            )}
          </div>
        )}

        {tab === 'audit' && (
          <div className="p-6">
            {!isNew && withdrawal ? (
              <AuditViewer entityName="BankWithdrawal" entityId={withdrawal.id} />
            ) : (
              <div className="flex flex-col items-center justify-center h-48 text-slate-400">
                <ShieldCheck size={28} className="mb-2 opacity-30" />
                <p className="text-sm">Save the withdrawal first to view audit history.</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Action modals */}
      {modal === 'approve' && (
        <ActionModal title="Approve Withdrawal" placeholder="Approval remarks (optional)…" required={false}
          onConfirm={text => { setModal(null); handleApprove(text); }} onCancel={() => setModal(null)} />
      )}
      {modal === 'reject' && (
        <ActionModal title="Reject Withdrawal" placeholder="Rejection reason (required)…" required={true}
          onConfirm={text => { setModal(null); handleReject(text); }} onCancel={() => setModal(null)} />
      )}
      {modal === 'reverse' && (
        <ActionModal title="Reverse Withdrawal" placeholder="Reversal narration (optional)…" required={false}
          onConfirm={text => { setModal(null); handleReverse(text); }} onCancel={() => setModal(null)} />
      )}
      {modal === 'cancel' && (
        <ActionModal title="Cancel Withdrawal" placeholder="Cancellation reason (required)…" required={true}
          onConfirm={text => { setModal(null); handleCancel(text); }} onCancel={() => setModal(null)} />
      )}
      {modal === 'delete' && (
        <ActionModal title="Delete Draft Withdrawal"
          placeholder="The draft is cancelled and kept for audit. It can no longer be edited or posted."
          required={false}
          onConfirm={() => { setModal(null); handleDelete(); }}
          onCancel={() => setModal(null)} />
      )}

      {/* Search modals */}
      <MasterSearchModal<BankAccountListDto>
        isOpen={bankOpen}
        onClose={() => setBankOpen(false)}
        title="Select Bank Account"
        columns={BANK_ACCOUNT_COLUMNS}
        fetchData={fetchBankAccounts}
        onSelect={item => {
          setBankAccountId(item.id);
          setBankAccountNumber(item.accountNumber);
          setBankAccountName(item.accountName);
          setBankAccountCode(item.accountNumber);
          if (item.currencyCode) setCurrencyCode(item.currencyCode);
          setBankOpen(false);
        }}
      />

      <MasterSearchModal<AccountListDto>
        isOpen={contraOpen}
        onClose={() => setContraOpen(false)}
        title="Select Contra (GL) Account"
        columns={GL_ACCOUNT_COLUMNS}
        fetchData={fetchGlAccounts}
        onSelect={item => {
          setContraAccountId(item.id);
          setContraAccountCode(item.code);
          setContraAccountName(item.name);
          setContraOpen(false);
        }}
      />

      {/* Print */}
      {withdrawal && (
        <MasterReportPreview
          isOpen={printOpen}
          onClose={() => setPrintOpen(false)}
          title={`Bank Withdrawal ${withdrawal.withdrawalNumber}`}
          fileBaseName={`Bank-Withdrawal-${withdrawal.withdrawalNumber}`}
          email={{ documentType: 'Bank Withdrawal', documentNo: withdrawal.withdrawalNumber }}
          onDownloadExcel={() => exportToExcel({
            sheetName: 'Bank Withdrawal',
            title: `Bank Withdrawal — ${withdrawal.withdrawalNumber}`,
            columns: [
              { header: 'Field', key: 'field', width: 28 },
              { header: 'Value', key: 'value', width: 40 },
            ],
            rows: [
              { field: 'Document No',     value: withdrawal.withdrawalNumber ?? '' },
              { field: 'Withdrawal Date', value: withdrawal.withdrawalDate?.slice(0, 10) ?? '' },
              { field: 'Posting Date',    value: withdrawal.postingDate?.slice(0, 10) ?? '' },
              { field: 'Bank Account',    value: `${withdrawal.bankAccountCode ?? ''} ${withdrawal.bankAccountName ?? ''}`.trim() },
              { field: 'Withdrawal Type', value: withdrawal.withdrawalType ?? '' },
              { field: 'Contra Account',  value: `${withdrawal.contraAccountCode ?? ''} ${withdrawal.contraAccountName ?? ''}`.trim() },
              { field: 'Amount',          value: withdrawal.amount ?? 0 },
              { field: 'Currency',        value: withdrawal.currencyCode ?? '' },
              { field: 'Exchange Rate',   value: withdrawal.exchangeRate ?? 1 },
              { field: 'Base Amount',     value: withdrawal.baseAmount ?? 0 },
              { field: 'Reference',       value: withdrawal.reference ?? '' },
              { field: 'Narration',       value: withdrawal.narration ?? '' },
            ],
          }, `Bank-Withdrawal-${withdrawal.withdrawalNumber}`)}
        >
          <BankWithdrawalVoucher
            withdrawal={withdrawal}
            companyName={authUser?.companyId ? '' : 'myLedger ONE'}
            printedBy={authUser?.username}
          />
        </MasterReportPreview>
      )}
    </div>
  );
};
